import { Box, Button, FormHelperText, FormLabel, Stack, Typography } from '@mui/material';
import Modal from 'components/@home/modals/Modal';
import Autosuggest from 'components/controls/Autosuggest';
import levels from 'components/@whistleblower-admin/levels';
import FormikPermissions from 'components/@whistleblower-admin/pages/Users/FormikPermissions';
import { Field, Formik } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { I18n } from 'utils/i18n';
import { usePatchUserMutation, usePostUserMutation } from 'store/app/entities/WbSlice';
import { useLazyListVisibleQuery } from 'store/app/entities/EmployeesApiSlice';
import PropTypes from 'prop-types';

const CONFLICT = 409;

const PAGE_SIZE = 25;

const WbUserDialog = ({ isOpen, close, user, assignedEmployeeIds = [] }) => {
  const isEdit = !!user;
  const [create] = usePostUserMutation();
  const [patch] = usePatchUserMutation();
  const [triggerVisible] = useLazyListVisibleQuery();
  const assignedSet = useMemo(
    () => new Set(assignedEmployeeIds.map(id => String(id))),
    [assignedEmployeeIds],
  );

  const loadEmployeeOptions = useCallback(
    async (search, _loadedOptions, additional) => {
      const page = additional?.page ?? 0;
      const skip = page * PAGE_SIZE;
      try {
        const raw = await triggerVisible({
          search: search || '',
          skip,
          limit: PAGE_SIZE,
          order: 'name',
        }).unwrap();
        const employees = raw?.employees ?? [];
        const total = raw?.metadata?.total ?? 0;
        const options = employees
          .filter(e => !e.isApp && !assignedSet.has(String(e._id)))
          .map(e => ({
            label: [e.firstName, e.surName].filter(Boolean).join(' ') || e.name || e.code || '',
            value: e._id,
          }))
          .filter(o => o.label);
        const loaded = skip + employees.length;
        const hasMore = loaded < total;
        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch {
        return {
          options: [],
          hasMore: false,
          additional: { page: 0 },
        };
      }
    },
    [triggerVisible, assignedSet],
  );

  return (
    <Modal isOpen={isOpen} onClose={close} aria-labelledby="wb-user-dialog-title">
      <Box sx={{ minWidth: 600, maxWidth: '90vh', width: '100%' }}>
        <Typography
          id="wb-user-dialog-title"
          component="h2"
          level="inherit"
          sx={{
            fontSize: '1.25em',
            mb: '0.25em',
          }}
        >
          {I18n.t(`WhistleblowerPortalConfig.WbUserDialog.Title${isEdit ? 'Edit' : 'Add'}`, {
            name: user?.employee?.name || '',
          })}
        </Typography>
        {!!isOpen && (
          <Formik
            enableReinitialize
            initialValues={{
              employee:
                isEdit && user?.employee?._id
                  ? {
                      label: user.employee.name || '',
                      value: user.employee._id,
                    }
                  : '',
              permissions: isEdit
                ? user.permissions
                : {
                    portals: levels.none,
                    occurrences: levels.writer,
                    users: levels.none,
                  },
            }}
            validate={values => {
              const errors = {};
              if (!isEdit) {
                const emp = values.employee;
                const missing = !emp || (typeof emp === 'object' && !emp.value);
                if (missing) {
                  errors.user = I18n.t('WhistleblowerPortalConfig.errors.Required', {
                    field: I18n.t('WhistleblowerPortalConfig.atts.user'),
                  });
                }
              }
              return errors;
            }}
            onSubmit={async (values, { setSubmitting, setFieldError }) => {
              const employeeId =
                typeof values.employee === 'object' && values.employee?.value
                  ? values.employee.value
                  : values.employee;
              const { error } = isEdit
                ? await patch({ id: user._id, permissions: values.permissions })
                : await create({ permissions: values.permissions, employee: employeeId });
              setSubmitting(false);
              if (error) {
                if (error.status === CONFLICT) {
                  error.data?.key?.forEach(field => {
                    setFieldError(
                      field,
                      I18n.t('WhistleblowerPortalConfig.errors.Duplicate', {
                        field: I18n.t(`WhistleblowerPortalConfig.atts.${field}`).toLowerCase(),
                      }),
                    );
                  });
                }
              } else {
                close();
              }
            }}
          >
            {({ errors, handleSubmit, isSubmitting, submitCount }) => (
              <form onSubmit={handleSubmit}>
                <Stack spacing={1} sx={{ mt: 4, mb: 2, width: '100%', alignItems: 'stretch' }}>
                  {!isEdit && (
                    <Stack spacing={0.5} sx={{ width: '100%', minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 500 }}>
                        {I18n.t('WhistleblowerPortalConfig.atts.user')}
                      </Typography>
                      <FormHelperText error component="div" sx={{ minHeight: 20 }}>
                        {errors?.user && submitCount > 0 ? errors.user : '\u00a0'}
                      </FormHelperText>
                      <Box sx={{ width: '100%', minWidth: 0 }}>
                        <Field name="employee">
                          {({ field, form }) => (
                            <Autosuggest
                              fullWidth
                              loadOptions={loadEmployeeOptions}
                              additional={{ page: 0 }}
                              debounceTimeout={300}
                              value={field.value}
                              onChange={opt =>
                                form.setFieldValue(
                                  'employee',
                                  opt ? { label: opt.label, value: opt.value } : '',
                                )
                              }
                              onBlur={field.onBlur}
                              name={field.name}
                              placeholder={I18n.t('SelectPlaceholder')}
                              error={!!(errors.user && submitCount > 0)}
                              styles={{
                                container: base => ({
                                  ...base,
                                  width: '100%',
                                  minWidth: 0,
                                }),
                                control: base => ({
                                  ...base,
                                  minWidth: 0,
                                }),
                                menu: provided => ({
                                  ...provided,
                                  minWidth: 'min(100%, 560px)',
                                  width: '100%',
                                }),
                              }}
                            />
                          )}
                        </Field>
                      </Box>
                    </Stack>
                  )}

                  <FormLabel sx={{ fontWeight: 500 }}>
                    {I18n.t('WhistleblowerPortalConfig.atts.permissions')}
                  </FormLabel>
                  <Field component={FormikPermissions} name="permissions" />

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button type="button" onClick={close} variant="outlined" color="secondary">
                      {I18n.t('WhistleblowerPortalConfig.WbUserDialog.Cancel')}
                    </Button>
                    <Button disabled={isSubmitting} type="submit" variant="contained">
                      {I18n.t(
                        `WhistleblowerPortalConfig.WbUserDialog.${isEdit ? 'Edit' : 'Create'}`,
                      )}
                    </Button>
                  </Box>
                </Stack>
              </form>
            )}
          </Formik>
        )}
      </Box>
    </Modal>
  );
};

WbUserDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  /** Set when editing an existing whistleblower user; omit when adding. */
  user: PropTypes.object,
  /** CompanyUser ids that already have a `WbUser` row (exclude from add picker). */
  assignedEmployeeIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
};

export default WbUserDialog;
